import {t} from 'i18next';
import {useState, useEffect, useRef} from 'react';

interface AurDateSpanProps {
  value: string;
  type:
    | 'startYear'
    | 'startMonth'
    | 'startDay'
    | 'endYear'
    | 'endMonth'
    | 'endDay';
  onClick: (
    type:
      | 'startYear'
      | 'startMonth'
      | 'startDay'
      | 'endYear'
      | 'endMonth'
      | 'endDay',
    event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
  ) => void;
  onChange: (type: string, value: string) => void;
  onBlur?: (
    type: string,
    ref: React.RefObject<HTMLInputElement>,
    event?: React.FocusEvent<HTMLInputElement>
  ) => void;
  textFieldRef: React.RefObject<HTMLInputElement>;
  style?: React.CSSProperties;
  className?: string;
}

const YEAR_LENGTH = 4;
const MONTH_DAY_LENGTH = 2;

const AurDateSpan: React.FC<AurDateSpanProps> = ({
  value,
  type,
  onClick,
  onChange,
  textFieldRef,
  style = {},
  className = '',
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const isMouseDown = useRef<boolean>(false);
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (/^\d*$/.test(newValue)) {
      const maxLength = type.includes('Year') ? YEAR_LENGTH : MONTH_DAY_LENGTH;

      if (newValue.length > maxLength) {
        newValue = newValue.slice(-maxLength);
      }

      if (type.includes('Day') && parseInt(newValue) > 31) {
        return;
      }
      if (type.includes('Month') && parseInt(newValue) > 12) {
        return;
      }

      setInputValue(newValue);
      if (newValue === '0000' && type.includes('Year')) {
        onChange(type, 'YYYY');
      } else if (newValue === '00') {
        onChange(type, type.includes('Month') ? 'MM' : 'DD');
      } else {
        onChange(type, newValue.padStart(maxLength, '0'));
      }
    } else {
      // Added placeholder 0000 or 00 as per type and replace it with user's valid digit input
      const maxLength = type.includes('Year') ? YEAR_LENGTH : MONTH_DAY_LENGTH;
      const placeholder = type.includes('Year')
        ? t('common.dateSpan.fourZeros')
        : t('common.dateSpan.twoZeros');
      const inputValue = e.target.value.replace(/[^0-9]/g, ''); // Allow only numbers

      // Limit the value to the specified max length
      const truncatedValue = inputValue.slice(0, maxLength);

      const placeholderArray = placeholder.split('');

      // Fill the placeholder array from the end with input values
      for (let i = 0; i < truncatedValue.length; i++) {
        placeholderArray[placeholder.length - truncatedValue.length + i] =
          truncatedValue[i];
      }
      setInputValue(placeholderArray.join(''));
      onChange(type, placeholderArray.join(''));
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
    if (isMouseDown.current) {
      isMouseDown.current = false;
      return;
    }
    onClick(type, e, textFieldRef);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(type, textFieldRef, e);
  };
  return (
    <input
      type="text"
      aria-label={`${type} ${t('common.dateSpan.input')}`}
      value={
        inputValue === '0000' && type.includes('Year')
          ? t('common.dateSpan.yyyy')
          : inputValue === '00' && !type.includes('Year')
            ? type.includes('Month')
              ? t('common.dateSpan.mm')
              : t('common.dateSpan.dd')
            : inputValue?.padStart(type.includes('Year') ? 4 : 2, '0')
      }
      onClick={(e) => onClick(type, e, textFieldRef)}
      onMouseDown={(_) => (isMouseDown.current = true)}
      onFocus={handleFocus}
      onChange={handleInputChange}
      onBlur={handleBlur}
      ref={textFieldRef}
      className={className}
      style={{
        ...style,
        border: 'none',
        backgroundColor: 'transparent',
        color: '#F2F4F7',
        textAlign: 'center',
      }}
    />
  );
};

export default AurDateSpan;
